import { useQuery } from "@tanstack/react-query";
import { backendService } from "../services/backend.service";
import type { PickupApprovalItem } from "../utils/models";

/** Keep the startup-prefetched queue fresh long enough that opening the panel serves it without a refetch flash. */
const RECOMMENDATIONS_STALE_MS = 60_000;

interface AnnieRecommendationsResult {
	data: PickupApprovalItem[] | undefined;
	isLoading: boolean;
	isError: boolean;
	refetch: () => void;
}

/**
 * Fetches the head-nurse pending-approval queue that seeds Annie's open screen (top-3 rows, soonest-first —
 * the backend already sorts). The always-mounted panel calls this once the department + head-nurse role are
 * known, so the query is warmed at app startup; a short `staleTime` then serves that cached result when the
 * panel opens, avoiding a loading flash. The backend enforces the head-nurse gate; the caller passes `enabled`
 * to keep the request off for non-head-nurses.
 */
export function useAnnieRecommendations(departmentId: string, enabled: boolean): AnnieRecommendationsResult {
	const { data, isLoading, isError, refetch } = useQuery({
		queryKey: ["annie-recommendations", departmentId],
		queryFn: async () => {
			const response = await backendService.getAnnieRecommendations(departmentId);
			return response.data.recommendations;
		},
		enabled: enabled && !!departmentId,
		staleTime: RECOMMENDATIONS_STALE_MS,
	});

	return { data, isLoading, isError, refetch };
}
